/**
 * AssetCreateWizard — Manuelle Anlage (für Altbestand ohne Beleg)
 *
 * Geöffnet aus /anlagen Header-Button. Komplett manuell, ohne Expense-Bezug.
 */
import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Plus, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import AfaPlanPreviewTable from "./AfaPlanPreviewTable";
import { getVatRatesForCountry } from "@/utils/vatRates";

export default function AssetCreateWizard({ open, onClose, country = "DE", onCreated, prefill = null }) {
  const [table, setTable] = useState({ entries: [], gwg: {} });
  // Feb 2026 — Country-Defaults: VAT-Rate muss in den Länder-Presets liegen, sonst zeigt das
  // Dropdown "Individuell" obwohl der User noch nichts geändert hat.
  const countryDefaultVat = country === "AT" ? 20 : country === "CH" ? 8.1 : 19;
  const [form, setForm] = useState({
    name: "",
    category_key: "computer_notebook",
    acquisition_date: new Date().toISOString().slice(0, 10),
    depreciation_start_date: new Date().toISOString().slice(0, 10),
    cost_net: 0,
    cost_gross: 0,
    vat_amount: 0,
    vat_rate: countryDefaultVat,
    vendor: "",
    useful_life_years: 3,
    depreciation_method: country === "CH" ? "degressive" : "linear",
    degressive_rate: country === "CH" ? 0.40 : null,
    residual_value: 0,
    private_use_percentage: 0,
    notes: "",
    receipt_file_key: null,
    receipt_filename: null,
    receipt_storage_uri: null,
    receipt_content_type: null,
  });
  const [saving, setSaving] = useState(false);
  const vatRates = getVatRatesForCountry(country);

  // Beim Country-Wechsel den VAT-Default nachziehen, falls User noch nichts angefasst hat
  useEffect(() => {
    setForm((f) => {
      const isAtDefault = f.cost_gross === 0 && (f.vat_rate === 19 || f.vat_rate === 20 || f.vat_rate === 8.1);
      if (!isAtDefault) return f;
      return { ...f, vat_rate: countryDefaultVat };
    });
  }, [country, countryDefaultVat]);

  useEffect(() => {
    if (!open) return;
    api
      .get(`/afa-table?country=${country}`)
      .then((res) => setTable(res.data))
      .catch(() => setTable({ entries: [], gwg: {} }));
  }, [open, country]);

  // Prefill (from receipt upload OCR result) anwenden, sobald sich prefill ändert
  useEffect(() => {
    if (!open || !prefill) return;
    // VAT-Rate konsistent als Zahl normalisieren — String-Werte aus dem Backend brechen sonst === Vergleich
    const defaultRate = country === "AT" ? 20 : country === "CH" ? 8.1 : 19;
    const rawRate = prefill.vat_rate;
    const parsedRate = rawRate != null ? parseFloat(rawRate) : NaN;
    const prefilledRate = (!isNaN(parsedRate) && parsedRate > 0) ? parsedRate : null;
    setForm((f) => {
      const rate = prefilledRate ?? (f.vat_rate > 0 ? f.vat_rate : defaultRate);
      const gross = parseFloat(prefill.cost_gross || prefill.amount) || 0;
      const net = gross > 0 ? gross / (1 + rate / 100) : 0;
      return {
        ...f,
        name: prefill.name || prefill.description || prefill.vendor || f.name,
        vendor: prefill.vendor || f.vendor,
        acquisition_date: prefill.date || f.acquisition_date,
        depreciation_start_date: prefill.date || f.depreciation_start_date,
        cost_gross: gross || f.cost_gross,
        cost_net: gross > 0 ? parseFloat(net.toFixed(2)) : f.cost_net,
        vat_amount: gross > 0 ? parseFloat((gross - net).toFixed(2)) : f.vat_amount,
        vat_rate: rate,
        category_key: prefill.suggested_category || f.category_key,
        receipt_file_key: prefill.receipt?.file_key || null,
        receipt_filename: prefill.receipt?.filename || null,
        receipt_storage_uri: prefill.receipt?.storage_uri || null,
        receipt_content_type: prefill.receipt?.content_type || null,
      };
    });
    // Custom-Mode synchronisieren: Wenn der erkannte/Default-Satz in den Länder-Standards liegt → Dropdown, sonst Individuell
    const finalRate = prefilledRate ?? defaultRate;
    setShowCustomVat(!vatRates.some((r) => r.rate === finalRate));
  }, [open, prefill, country, vatRates]);

  // Wenn cost_gross ändert → cost_net + vat_amount neu rechnen
  const setGross = (g) => {
    const gross = parseFloat(g) || 0;
    const rate = form.vat_rate || 19;
    const net = gross / (1 + rate / 100);
    setForm({ ...form, cost_gross: gross, cost_net: parseFloat(net.toFixed(2)), vat_amount: parseFloat((gross - net).toFixed(2)) });
  };

  const onCategoryChange = (key) => {
    const entry = table.entries.find((e) => e.key === key);
    setForm((f) => ({
      ...f,
      category_key: key,
      useful_life_years: entry?.years || f.useful_life_years,
      depreciation_method: entry?.method || f.depreciation_method,
      degressive_rate: entry?.rate ?? null,
    }));
  };

  // Default-Rate setzen wenn User method=degressive wählt aber keine Rate gesetzt ist (Bug-Fix Feb 2026)
  const handleMethodChange = (v) => {
    setForm((f) => {
      let rate = f.degressive_rate;
      if (v === "degressive" && !rate) {
        if (country === "CH") rate = 0.40;
        else if (country === "AT") rate = 0.30;
        else rate = Math.min(2.5 / Math.max(f.useful_life_years || 1, 1), 0.25);
        rate = Math.round(rate * 10000) / 10000;
      }
      return { ...f, depreciation_method: v, degressive_rate: rate };
    });
  };

  // ── Feb 2026 — Manuelles Anlegen (KEIN Auto-Save mehr!) ──
  // Der User füllt das Formular vollständig aus und klickt "Anlegen".
  // Blur-/Enter-Save komplett entfernt — war für die UX hier falsch.
  // Backend-Idempotency via client_create_id bleibt als Schutz gegen Doppelklick.
  const clientCreateIdRef = React.useRef(null);
  const [showCustomVat, setShowCustomVat] = React.useState(false);

  // Reset state beim Open/Close-Cycle
  useEffect(() => {
    if (open) {
      // Neuer Wizard-Session → neuer Idempotency-Key
      clientCreateIdRef.current = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `wiz_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      // Bei Prefill: wenn vat_rate in Standard-Sätzen liegt → Dropdown, sonst Custom-Mode
      const initialRate = prefill?.vat_rate;
      const isCustom = initialRate != null && !vatRates.some((r) => r.rate === initialRate);
      setShowCustomVat(isCustom);
    }
  }, [open]); // intentional: prefill/vatRates werden separat in eigenem Effect verarbeitet

  // Feb 2026 — Derived Mode: "Individuell" entweder explizit gewählt ODER aktueller Rate ist kein Preset
  // → garantiert, dass der Manual-Input IMMER erscheint, wenn das Dropdown "Individuell" zeigt
  const isCustomVatMode = showCustomVat || !vatRates.some((r) => r.rate === form.vat_rate);

  const handleCreate = async () => {
    if (saving) return; // Doppelklick-Schutz
    if (!form.name || form.name.trim().length < 3) {
      toast.error("Bitte eine Bezeichnung eingeben (min. 3 Zeichen)");
      return;
    }
    if (!form.cost_gross || form.cost_gross <= 0) {
      toast.error("Bitte einen Brutto-Betrag größer 0 eingeben");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, client_create_id: clientCreateIdRef.current };
      const res = await api.post("/fixed-assets", payload);
      if (!res.data.duplicate_prevented) {
        toast.success(`Anlage „${form.name}" angelegt · ${res.data.postings} AfA-Buchungen vorgemerkt`);
      } else {
        toast.success("Anlage gespeichert");
      }
      onCreated?.(res.data.asset);
      onClose?.();
    } catch (err) {
      toast.error(`Anlegen fehlgeschlagen: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="asset-create-wizard">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-emerald-600" />
            Anlage manuell erfassen
          </SheetTitle>
          <SheetDescription>
            Für Altbestand ohne Beleg-Bezug. Der Beleg-Trail muss separat aufbewahrt werden (GoBD).
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 pt-4 pb-8">
          {form.receipt_file_key ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex gap-2 text-sm" data-testid="receipt-attached-banner">
              <FileText className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-emerald-800">
                <strong>Beleg verknüpft:</strong> {form.receipt_filename || "Beleg"} — wird mit der Anlage gespeichert (GoBD-konform).
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 text-sm">
              <FileText className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-amber-800">
                Diese Anlage wird als <strong>„manuell erfasst"</strong> markiert (kein Beleg verknüpft).
                Lade den Original-Beleg im{" "}
                <a
                  href="/steuerberater-bereich?tab=tresor"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium hover:text-amber-900"
                  data-testid="tresor-link"
                >
                  Dokumente
                </a>
                {" "}separat hoch, um GoBD-konform aufbewahrt zu bleiben.
              </div>
            </div>
          )}

          <div>
            <Label>Bezeichnung</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="z.B. Bürostuhl Steelcase" data-testid="create-name" />
          </div>

          <div>
            <Label>Kategorie</Label>
            <Select value={form.category_key} onValueChange={onCategoryChange}>
              <SelectTrigger data-testid="create-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {table.entries.map((e) => (
                  <SelectItem key={e.key} value={e.key}>{e.label} — {e.years} J</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Anschaffungsdatum</Label>
              <Input type="date" value={form.acquisition_date}
                     onChange={(e) => setForm({ ...form, acquisition_date: e.target.value, depreciation_start_date: e.target.value })} />
            </div>
            <div>
              <Label>AfA-Beginn</Label>
              <Input type="date" value={form.depreciation_start_date}
                     onChange={(e) => setForm({ ...form, depreciation_start_date: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Brutto (€)</Label>
              <Input type="number" step="0.01" value={form.cost_gross} onChange={(e) => setGross(e.target.value)}
                     data-testid="create-cost-gross" />
            </div>
            <div>
              <Label>USt-Satz</Label>
              <Select
                value={isCustomVatMode ? "__custom__" : String(form.vat_rate)}
                onValueChange={(v) => {
                  if (v === "__custom__") {
                    setShowCustomVat(true);
                    return;
                  }
                  setShowCustomVat(false);
                  const rate = parseFloat(v);
                  setForm((f) => {
                    const gross = parseFloat(f.cost_gross) || 0;
                    const net = gross / (1 + rate / 100);
                    return {
                      ...f,
                      vat_rate: rate,
                      cost_net: parseFloat(net.toFixed(2)),
                      vat_amount: parseFloat((gross - net).toFixed(2)),
                    };
                  });
                }}
              >
                <SelectTrigger data-testid="create-vat-rate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {vatRates.map((r) => (
                    <SelectItem key={r.rate} value={String(r.rate)}>{r.label}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">Individuell…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Netto</Label>
              <Input value={`${form.cost_net.toFixed(2)} €`} disabled />
            </div>
          </div>

          {/* Manuelle USt-Eingabe: sichtbar wenn "Individuell" aktiv ODER aktueller Rate nicht in Presets */}
          {isCustomVatMode && (
            <div className="-mt-1" data-testid="create-vat-rate-custom-row">
              <Label>Individueller USt-Satz (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={50}
                  step="0.1"
                  value={Number.isFinite(form.vat_rate) ? form.vat_rate : ""}
                  onChange={(e) => {
                    const str = e.target.value;
                    // Leere Eingabe erlauben (= 0% temporär), sonst parsen
                    const rate = str === "" ? 0 : parseFloat(str);
                    if (isNaN(rate)) return; // intermediäre "abc" o.ä. ignorieren
                    setForm((f) => {
                      const gross = parseFloat(f.cost_gross) || 0;
                      const net = rate > 0 ? gross / (1 + rate / 100) : gross;
                      return {
                        ...f,
                        vat_rate: rate,
                        cost_net: parseFloat(net.toFixed(2)),
                        vat_amount: parseFloat((gross - net).toFixed(2)),
                      };
                    });
                  }}
                  className="max-w-[140px]"
                  data-testid="create-vat-rate-custom"
                  placeholder="z.B. 5.5"
                  autoFocus
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nutzungsdauer (J)</Label>
              <Input type="number" min={1} max={50} value={form.useful_life_years}
                     onChange={(e) => setForm({ ...form, useful_life_years: parseInt(e.target.value) || 1 })} />
            </div>
            <div>
              <Label>Methode</Label>
              <Select value={form.depreciation_method} onValueChange={handleMethodChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="linear">Linear</SelectItem>
                  <SelectItem value="degressive">Degressiv</SelectItem>
                  <SelectItem value="pool_5y" disabled={country !== "DE"}>Sammelposten 5J</SelectItem>
                  <SelectItem value="gwg_immediate">GWG Sofort</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Degressive-Rate-Input nur sichtbar wenn method=degressive */}
          {form.depreciation_method === "degressive" && (
            <div>
              <Label>Degressive Rate (% vom Buchwert/Jahr)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="1"
                  min={5}
                  max={50}
                  value={Math.round((form.degressive_rate || 0) * 100)}
                  onChange={(e) => setForm({ ...form, degressive_rate: parseFloat(e.target.value || 0) / 100 })}
                  className="max-w-[120px]"
                  data-testid="create-degressive-rate"
                />
                <span className="text-sm text-slate-500">% pro Jahr</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Standard: {country === "CH" ? "40 %" : country === "AT" ? "30 %" : "2,5× linear, max 25 %"}.
              </p>
            </div>
          )}

          <div>
            <Label>Privat-Anteil: <strong>{form.private_use_percentage} %</strong></Label>
            <Slider value={[form.private_use_percentage]} onValueChange={(v) => setForm({ ...form, private_use_percentage: v[0] })}
                    min={0} max={99} step={5} />
          </div>

          <AfaPlanPreviewTable
            costNet={form.cost_net}
            country={country}
            method={form.depreciation_method}
            years={form.useful_life_years}
            startDate={form.depreciation_start_date}
            residual={form.residual_value}
            degressiveRate={form.degressive_rate}
            privateUsePercentage={form.private_use_percentage}
          />

          {/* IAB/IFB-Info-Banner (Phase 5, MVP-Light gemäß Frage 9A) */}
          {(country === "DE" || country === "AT") && form.cost_net >= 800 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="iab-ifb-banner">
              <div className="font-medium mb-0.5">
                💡 {country === "DE" ? "IAB nach § 7g EStG" : "IFB nach § 11 EStG"} möglich
              </div>
              <div>
                {country === "DE"
                  ? "Bis zu 50 % der Anschaffungskosten könnten als Investitionsabzugsbetrag (IAB) bereits im Anschaffungsjahr abgesetzt werden — bei Erfüllung der Voraussetzungen."
                  : "Bis zu 15 % der Anschaffungskosten können als Investitionsfreibetrag (IFB) im Anschaffungsjahr zusätzlich abgesetzt werden."}
                {" "}Bitte mit Steuerberater klären.
              </div>
            </div>
          )}

          {/* Aktionen (Feb 2026 — manuelles Anlegen, KEIN Auto-Save) */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="close-create-btn">
                Abbrechen
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleCreate} disabled={saving || !form.name || form.cost_gross <= 0}
                      data-testid="create-asset-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Anlegen
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
